import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { ZONE_BORDER_COLORS } from './mapTypes';
import type { LocationMapItem } from './mapTypes';

interface MapZoneProps {
  item: LocationMapItem;
  cellPx: number;
  isSelected: boolean;
  colorIndex: number;
  onSelect: (id: string) => void;
  onDragStart: (id: string, clientX: number, clientY: number) => void;
  onResizeStart: (id: string, clientX: number, clientY: number) => void;
}

const MapZone: React.FC<MapZoneProps> = ({
  item, cellPx, isSelected, colorIndex, onSelect, onDragStart, onResizeStart,
}) => {
  const borderColor = ZONE_BORDER_COLORS[colorIndex % ZONE_BORDER_COLORS.length];

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1) return;
    e.stopPropagation();
    onSelect(item.id);
    onDragStart(item.id, e.clientX, e.clientY);
  }, [item.id, onSelect, onDragStart]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1) return;
    e.stopPropagation();
    onSelect(item.id);
    onResizeStart(item.id, e.clientX, e.clientY);
  }, [item.id, onSelect, onResizeStart]);

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        position: 'absolute',
        left: item.gridX * cellPx,
        top: item.gridY * cellPx,
        width: item.gridWidth * cellPx,
        height: item.gridHeight * cellPx,
        bgcolor: item.color || 'rgba(33, 150, 243, 0.18)',
        border: `2px ${isSelected ? 'solid' : 'dashed'} ${borderColor}`,
        borderRadius: '3px',
        cursor: 'move',
        zIndex: 1,
        boxSizing: 'border-box',
        transition: isSelected ? 'none' : 'box-shadow 0.15s',
        boxShadow: isSelected ? `0 0 0 2px ${borderColor}44` : 'none',
        '&:hover': { boxShadow: `0 0 0 2px ${borderColor}33` },
      }}
    >
      <Typography
        className="map-item-label"
        variant="caption"
        sx={{
          position: 'absolute',
          top: 2, left: 4,
          fontWeight: 600,
          fontSize: '0.7rem',
          color: borderColor,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {item.name}
      </Typography>

      {/* Resize handle */}
      <Box
        onMouseDown={handleResizeMouseDown}
        sx={{
          position: 'absolute',
          right: -4, bottom: -4,
          width: 10, height: 10,
          bgcolor: borderColor,
          borderRadius: '2px',
          cursor: 'nwse-resize',
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.15s',
          zIndex: 2,
          '&:hover': { opacity: 1 },
        }}
      />
    </Box>
  );
};

export default React.memo(MapZone);
